"use client";

import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Link from "next/link";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { AuthPageHeader } from "../page-header";
import { SubmitButton } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForgotPassword } from "../../hooks/use-forgot-password";
import { useCooldown } from "@/hooks/use-cooldown";
import { Icon } from "@/components/ui/icon";

const schema = z.object({
  email: z.email("Please enter a valid email address"),
});

type ForgotValues = z.infer<typeof schema>;

export function ForgotPassword() {
  const [sent, setSent] = useState(false);
  const {
    cooldown,
    start: startCooldown,
    isActive: cooldownActive,
  } = useCooldown(60);
  const forgotPassword = useForgotPassword();

  const form = useForm<ForgotValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "" },
  });

  function onSubmit(values: ForgotValues) {
    forgotPassword.mutate(values, {
      onSuccess: () => {
        setSent(true);
        startCooldown();
      },
    });
  }

  function handleResend() {
    if (cooldownActive || forgotPassword.isPending) return;
    forgotPassword.mutate(
      { email: form.getValues("email") },
      { onSuccess: startCooldown },
    );
  }

  return (
    <>
      <AuthPageHeader
        title="Reset your password"
        subtitle={
          sent
            ? "Check your email for a password reset link. Only the most recent link works and it expires in 15 minutes."
            : "Enter your email and we\u2019ll send you a reset link."
        }
      />

      {sent ? (
        <div className="flex flex-col gap-4 items-center">
          <div className="flex items-center gap-3 bg-contrast/4 border border-contrast/1 rounded-md px-3 py-2.5 w-full">
            <div className="size-10 shrink-0 rounded-md bg-contrast/8 border border-contrast/1 flex items-center justify-center">
              <Icon
                name="IconEmail1"
                size={20}
                className="text-heading opacity-60"
              />
            </div>
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-sm font-medium text-heading leading-5">
                Check your inbox
              </span>
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldown > 0 || forgotPassword.isPending}
                className="text-xs text-[#5b8def] hover:text-[#7ba5f5] transition-colors leading-4 text-left cursor-pointer disabled:text-contrast/30 disabled:cursor-not-allowed"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend reset"}
              </button>
            </div>
          </div>
          <Link
            href="/login"
            className="text-sm text-contrast/50 hover:text-contrast/70 transition-colors flex items-center gap-1 leading-5"
          >
            <Icon name="IconArrowLeft" size={14} />
            Back to login
          </Link>
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel required>Email Address</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      variant="auth"
                      size="default"
                      placeholder="mail@neuroscale.ai"
                      autoComplete="email"
                      className="rounded-sm"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-3">
              <SubmitButton
                type="submit"
                variant="linear-ai"
                size="auth"
                loading={forgotPassword.isPending}
              >
                Send reset link <Icon name="IconArrowRight" size={16} />
              </SubmitButton>

              <Link
                href="/login"
                className="text-sm text-contrast/50 hover:text-contrast/70 transition-colors flex items-center justify-center gap-1 leading-5"
              >
                <Icon name="IconArrowLeft" size={14} />
                Back to login
              </Link>
            </div>
          </form>
        </Form>
      )}
    </>
  );
}
